import React from 'react';
import { Link } from 'react-router-dom';
import { Images, Cpu, Boxes } from 'lucide-react';

import PageMasthead from '../components/PageMasthead';
import SiteFooter from '../components/SiteFooter';
import SiteHeader from '../components/SiteHeader';

/**
 * CR3ATE TECHNOLOGY — the engine the three products share.
 *
 * Same shape as /desktop and /spatial-models: masthead and pitch on the left, one
 * boxed element on the right, a labelled band of three beneath, one closing line.
 * Where those pages box an offer, this one boxes the three ways to reach the
 * engine — this page sells nothing itself, it routes.
 *
 * The one-pager's "why it holds up" section is folded into that closing line. It
 * carries the only claim a technical reader will actually weigh, so it stays; as
 * its own section it cost a scroll and said it twice.
 */

const CAPABILITIES = [
  {
    n: '01',
    label: 'Takes any input',
    Icon: Images,
    body: 'Noisy phone footage, shaky drone video, survey-grade aerial. Ground, '
        + 'air and indoor captures. Optional LiDAR, point clouds and poses. '
        + 'Room to city scale.',
  },
  {
    n: '02',
    label: 'State-of-the-art processing',
    Icon: Cpu,
    body: 'Combines AI models and classical photogrammetry in a single system, '
        + 'tuned to work together. Production code that keeps up with research '
        + 'without compromise.',
  },
  {
    n: '03',
    label: 'Outputs for every task',
    Icon: Boxes,
    body: 'Hierarchical Gaussians and .rad in a custom web viewer. High frame '
        + 'rates, studio-quality colour and full actor support with relighting '
        + 'through an Unreal Engine plugin. Export meshes, point clouds and '
        + 'Gaussians. Agentic reasoning built in.',
  },
] as const;

/* This page's job is to hand the reader to the surface that fits them. */
const SURFACES = [
  { to: '/desktop', line: 'Run it on your own machine.',        cta: 'Get Desktop App' },
  { to: '/reco',    line: 'Give us your images, get a 3D model.', cta: 'Try Images to 3D' },
  { to: '/sdk',     line: 'Build it into your own pipeline.',   cta: 'Get the SDK' },
] as const;

const Cr3ateTechnologyPage: React.FC = () => (
  <div className="flex min-h-dvh flex-col bg-ink-950 text-ink-100">
    <SiteHeader />

    <main className="relative flex-1">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-96 bg-[radial-gradient(60%_60%_at_50%_0%,rgba(12,180,228,0.09),transparent_70%)]"
      />

      <div className="relative mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:py-10">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1.15fr_1fr] lg:items-center lg:gap-12">
          <div>
            <PageMasthead
              title="Cr3ate Technology"
              tagline="Captured images → 3D reconstructions → spatial reasoning"
            />
            <p className="mt-6 text-balance font-brand text-xl font-light leading-snug tracking-tight text-ink-100 sm:text-2xl">
              Not just 3D reconstructions.
              <br />
              Spatial models that can reason.
            </p>
            <p className="mt-4 max-w-xl text-balance font-brand text-body font-light leading-relaxed text-ink-300">
              Alignment, depth, mesh, splat, measurement and reasoning, all from
              the same coherent processing pipeline.
            </p>
          </div>

          {/* The boxed element, in the slot /desktop gives its offer. Three
              destinations rather than one call to action: the engine is the
              product here, and the reader has to pick a surface. */}
          <div className="rounded-2xl border border-ember-500/40 bg-ember-500/[0.06] p-6 sm:p-7">
            <p className="font-brand text-meta font-medium uppercase tracking-[0.18em] text-ember-500">
              Three ways to put it to work
            </p>
            {/* Sentence then button, on one row. The buttons carry the same
                crane fill as the call to action on the other three pages, so a
                reader arriving from any of them recognises what to press. Two
                rows each would make this panel the tallest thing on the page and
                push it past a 720px window. */}
            {/* A two-column grid rather than three independent rows: the button
                column sizes to the widest label and `w-full` makes all three fill
                it, so they match without a hardcoded width that the next copy
                change would break. One column below sm, where the panel is too
                narrow for a sentence and a button side by side. */}
            <div className="mt-4 grid grid-cols-1 gap-x-3 gap-y-3 sm:grid-cols-[1fr_auto] sm:items-center sm:gap-y-2.5">
              {SURFACES.map(({ to, line, cta }) => (
                <React.Fragment key={to}>
                  <span className="text-ui font-light text-ink-300">{line}</span>
                  <Link
                    to={to}
                    className="inline-flex w-auto min-w-[8.75rem] items-center justify-center justify-self-start rounded-lg bg-crane-500 px-3.5 py-1.5 font-brand text-ui font-medium text-ink-950 transition-colors hover:bg-crane-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-crane-400 focus-visible:ring-offset-2 focus-visible:ring-offset-ink-950 sm:w-full"
                  >
                    {cta}
                  </Link>
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-5 border-t border-ink-800 pt-4 lg:mt-7">
          <h2 className="font-brand text-meta font-medium uppercase tracking-[0.18em] text-ember-500">
            What makes it different
          </h2>

          <div className="mt-5 grid grid-cols-1 gap-x-10 gap-y-6 sm:grid-cols-3">
            {CAPABILITIES.map(({ n, label, body, Icon }) => (
              <div key={n}>
                <div className="flex items-center gap-2.5">
                  <Icon className="h-5 w-5 flex-shrink-0 text-ember-500" aria-hidden />
                  <span className="font-brand text-meta font-medium uppercase tracking-[0.16em] text-ink-400">
                    {n}
                  </span>
                  <span className="font-brand text-title font-medium tracking-wide text-ink-100">
                    {label}
                  </span>
                </div>
                <p className="mt-2.5 text-ui font-light leading-relaxed text-ink-300">
                  {body}
                </p>
              </div>
            ))}
          </div>

          {/* The one non-obvious claim on the sheet, kept as a line rather than a
              section. It is why the mesh and the splat agree with each other. */}
          <p className="mt-5 max-w-3xl text-ui font-light leading-relaxed text-ink-400">
            <span className="text-ink-200">One metric frame, end to end, for
            class-leading consistency.</span>{' '}
            Every intermediate result is computed in the same coordinate frame, so
            exported meshes, rendered splats and point-cloud measurements all agree.
          </p>
        </div>
      </div>
    </main>

    <SiteFooter />
  </div>
);

export default Cr3ateTechnologyPage;
